/**
 * Cloud Attack Path Queries
 *
 * Pre-built queries for cloud infrastructure privilege escalation paths.
 * Currently focuses on AWS IAM attack primitives.
 *
 * To add new cloud query: Add query object to CLOUD_ATTACK_PATH_QUERIES array.
 * Query will automatically appear in defaultQueries.ts export.
 */

import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

import { QueryFolders } from './folders';

export const CLOUD_ATTACK_PATH_QUERIES: SavedQuery[] = [
  {
    id: 'aws-sts-assume-role',
    name: 'AWS STS AssumeRole + PutRolePolicy Privilege Escalations',
    type: 'graph',
    description:
      'Find AWS resources that can assume roles on other AWS resources via sts:AssumeRole permissions.',
    folderId: QueryFolders.CLOUD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['AWSResource'],
        filters: [
          {
            field: 'resourceType',
            operator: '=',
            value: 'AWS::Service',
            not: true,
          },
        ],
        relationships: [
          {
            label: 'IAM_AWS_PERMISSION',
            filters: [
              {
                field: 'actions',
                operator: 'ANY_STARTS_WITH',
                value: 'sts:AssumeRole',
              },
              {
                field: 'actions',
                operator: 'ANY_STARTS_WITH',
                value: 'iam:PutRolePolicy',
              },
            ],
            target: {
              labels: ['AWSResource'],
              filters: [
                {
                  field: 'resourceType',
                  operator: '=',
                  value: 'AWS::IAM::Role',
                },
              ],
            },
          },
        ],
      },
      limit: 5,
    },
  },
  {
    id: 'aws-user-attach-user-policy-self',
    name: 'AWS Users with iam:AttachUserPolicy on Themselves',
    type: 'graph',
    description:
      'Find AWS IAM users that can attach user policies to themselves via iam:AttachUserPolicy.',
    folderId: QueryFolders.CLOUD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['AWSResource'],
        filters: [
          {
            field: 'resourceType',
            operator: '=',
            value: 'AWS::IAM::User',
          },
        ],
        self_edges: 'require',
        relationships: [
          {
            label: 'IAM_AWS_PERMISSION',
            filters: [
              {
                field: 'actions',
                operator: 'ANY_STARTS_WITH',
                value: 'iam:AttachUserPolicy',
              },
            ],
            target: {
              labels: ['AWSResource'],
              filters: [
                {
                  field: 'resourceType',
                  operator: '=',
                  value: 'AWS::IAM::User',
                },
              ],
            },
          },
        ],
      },
      limit: 5,
    },
  },
  {
    id: 'aws-role-attach-role-policy-self',
    name: 'AWS Roles with iam:AttachRolePolicy on Themselves',
    type: 'graph',
    description:
      'Find AWS IAM roles that can attach role policies to themselves via iam:AttachRolePolicy.',
    folderId: QueryFolders.CLOUD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['AWSResource'],
        filters: [
          {
            field: 'resourceType',
            operator: '=',
            value: 'AWS::IAM::Role',
          },
        ],
        self_edges: 'require',
        relationships: [
          {
            label: 'IAM_AWS_PERMISSION',
            filters: [
              {
                field: 'actions',
                operator: 'ANY_STARTS_WITH',
                value: 'iam:AttachRolePolicy',
              },
            ],
            target: {
              labels: ['AWSResource'],
              filters: [
                {
                  field: 'resourceType',
                  operator: '=',
                  value: 'AWS::IAM::Role',
                },
              ],
            },
          },
        ],
      },
      limit: 5,
    },
  },
  {
    id: 'aws-principal-assume-role-policy-and-attach-role-policy',
    name: 'AWS Principals with sts:AssumeRolePolicy + iam:AttachRolePolicy on Target Role',
    type: 'graph',
    description:
      'Find IAM principals (roles, users, and groups) with both sts:AssumeRolePolicy and iam:AttachRolePolicy on an IAM role target.',
    folderId: QueryFolders.CLOUD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['AWSResource'],
        filters: [
          {
            field: 'resourceType',
            operator: 'IN',
            // ENG-3855: forward IN on a scalar property must wrap its values in
            // an inner array so the backend binds a Cypher list (`field IN $p`).
            // A flat array compiles to `field IN 'scalar'` and matches nothing.
            value: [['AWS::IAM::Role', 'AWS::IAM::User', 'AWS::IAM::Group']],
          },
        ],
        relationships: [
          {
            label: 'IAM_AWS_PERMISSION',
            filters: [
              {
                field: 'actions',
                operator: 'ANY_STARTS_WITH',
                value: 'sts:AssumeRolePolicy',
              },
              {
                field: 'actions',
                operator: 'ANY_STARTS_WITH',
                value: 'iam:AttachRolePolicy',
              },
            ],
            target: {
              labels: ['AWSResource'],
              filters: [
                {
                  field: 'resourceType',
                  operator: '=',
                  value: 'AWS::IAM::Role',
                },
              ],
            },
          },
        ],
      },
      limit: 5,
    },
  },
  {
    id: 'aws-user-put-user-policy-self',
    name: 'AWS Users with iam:PutUserPolicy on Themselves',
    type: 'graph',
    description:
      'Find AWS IAM users that can put inline user policies on themselves via iam:PutUserPolicy.',
    folderId: QueryFolders.CLOUD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['AWSResource'],
        filters: [
          {
            field: 'resourceType',
            operator: '=',
            value: 'AWS::IAM::User',
          },
        ],
        self_edges: 'require',
        relationships: [
          {
            label: 'IAM_AWS_PERMISSION',
            filters: [
              {
                field: 'actions',
                operator: 'ANY_STARTS_WITH',
                value: 'iam:PutUserPolicy',
              },
            ],
            target: {
              labels: ['AWSResource'],
              filters: [
                {
                  field: 'resourceType',
                  operator: '=',
                  value: 'AWS::IAM::User',
                },
              ],
            },
          },
        ],
      },
      limit: 5,
    },
  },
  {
    id: 'aws-all-iam-users',
    name: 'All IAM Users',
    type: 'table',
    description: 'List every AWS IAM user node.',
    folderId: QueryFolders.CLOUD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['AWSResource'],
        filters: [
          {
            field: 'resourceType',
            operator: '=',
            value: 'AWS::IAM::User',
          },
        ],
      },
    },
  },
  {
    id: 'aws-principal-assume-role-and-update-assume-role-policy',
    name: 'AWS Principals with sts:AssumeRole + iam:UpdateAssumeRolePolicy on Target Role',
    type: 'graph',
    description:
      'Find IAM principals (roles, users, and groups) with both sts:AssumeRole and iam:UpdateAssumeRolePolicy on an IAM role target.',
    folderId: QueryFolders.CLOUD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['AWSResource'],
        filters: [
          {
            field: 'resourceType',
            operator: 'IN',
            // ENG-3855: forward IN on a scalar property must wrap its values in
            // an inner array so the backend binds a Cypher list (`field IN $p`).
            // A flat array compiles to `field IN 'scalar'` and matches nothing.
            value: [['AWS::IAM::Role', 'AWS::IAM::User', 'AWS::IAM::Group']],
          },
        ],
        relationships: [
          {
            label: 'IAM_AWS_PERMISSION',
            filters: [
              {
                field: 'actions',
                operator: 'ANY_STARTS_WITH',
                value: 'sts:AssumeRole',
              },
              {
                field: 'actions',
                operator: 'ANY_STARTS_WITH',
                value: 'iam:UpdateAssumeRolePolicy',
              },
            ],
            target: {
              labels: ['AWSResource'],
              filters: [
                {
                  field: 'resourceType',
                  operator: '=',
                  value: 'AWS::IAM::Role',
                },
              ],
            },
          },
        ],
      },
      limit: 5,
    },
  },
];
